import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Flex, Box, Text } from 'rebass/styled-components';
import ReachabilityBadge from 'components/atoms/ReachabilityBadge';
import { REACH_LEVELS } from 'components/atoms/ReachabilityIndicator';
import ReachabilityPopover from 'components/molecules/ReachabilityPopover';

// Contacts-grid cell: a contact's aggregate Reachability (Figma "Reachability"
// column). The level + per-level connection counts are a BE field that is not
// emitted yet — see RELATIONSHIP_STRENGTH_INTRO_PATHS_BE_CONTRACT.md. When absent
// the cell renders a dash. The badge reveals the explainer popover on hover; it
// is portalled to the body so it is never clipped by the grid cell.
export const ReachabilityRenderer = ({ value, data }) => {
  const history = useHistory();
  const triggerRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const level = value?.level;
  if (!REACH_LEVELS.includes(level)) {
    return (
      <Flex p="8px" alignItems="center" sx={{ height: '100%' }}>
        <Text fontSize="14px" color="text.subtle">
          —
        </Text>
      </Flex>
    );
  }

  const counts = value?.counts ?? {};
  const introPathsUrl = data?.options?.contactIntroPathsUrl ?? data?.options?.contactUrl;
  const onSeeConnections = introPathsUrl ? () => history.push(introPathsUrl) : undefined;

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    // Anchor flush with the trigger; the visual gap lives inside the portal as
    // padding so moving the cursor onto the popover never crosses a dead zone.
    if (rect) setAnchor({ top: rect.bottom, left: rect.left });
  };

  return (
    <Flex p="8px" alignItems="center" sx={{ height: '100%' }}>
      <Box ref={triggerRef} onMouseEnter={open} onMouseLeave={() => setAnchor(null)} sx={{ display: 'inline-flex' }}>
        <ReachabilityBadge level={level} />
      </Box>
      {anchor &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              top: `${anchor.top}px`,
              left: `${anchor.left}px`,
              paddingTop: '4px',
              zIndex: 1000,
            }}
            onMouseEnter={open}
            onMouseLeave={() => setAnchor(null)}
          >
            <ReachabilityPopover
              level={level}
              counts={counts}
              total={value?.total}
              onSeeConnections={onSeeConnections}
            />
          </Box>,
          document.body,
        )}
    </Flex>
  );
};

ReachabilityRenderer.propTypes = {
  value: PropTypes.shape({
    level: PropTypes.string,
    counts: PropTypes.object,
    total: PropTypes.number,
  }),
  data: PropTypes.object,
};

ReachabilityRenderer.defaultProps = {
  value: null,
  data: {},
};
